import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wallet, CalendarClock, AlertTriangle, CheckCircle2, Clock,
  Plus, Pencil, Trash2, X, Save, Info, Receipt, Eye, Download,
} from 'lucide-react';
import InlineIntegrityChip from '../components/InlineIntegrityChip';
import { format } from 'date-fns';
import { businessToday } from '../lib/period';
import { newPaymentKey } from '../lib/paymentCommands';
import { usePermissions } from '../hooks/usePermissions';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { downloadCsv } from '../lib/csv';
import ObligationDetailDialog from '../components/ObligationDetailDialog';
import ObligationEffectsPanel from '../components/ObligationEffectsPanel';

const fmtINR = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return `\u20B9${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const dateLabel = (s) => {
  if (!s) return '—';
  try { return format(new Date(`${s}T00:00:00`), 'd MMM yyyy'); }
  catch (_e) { return s; }
};

const STATUS_STYLES = {
  OVERDUE:         { color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', label: 'Overdue' },
  DUE:             { color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', label: 'Due Today' },
  SCHEDULED:       { color: 'bg-zinc-800 text-zinc-300 border-zinc-700', label: 'Scheduled' },
  PARTIALLY_PAID:  { color: 'bg-sky-500/15 text-sky-300 border-sky-500/30', label: 'Partially Paid' },
  PAID:            { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Paid' },
  CANCELLED:       { color: 'bg-zinc-800 text-zinc-500 border-zinc-700', label: 'Cancelled' },
  DRAFT:           { color: 'bg-zinc-800 text-zinc-400 border-zinc-700', label: 'Draft' },
};

const SOURCE_TABS = [
  { key: 'OPERATING', label: 'Operating', description: 'Rent, Utilities, Marketing, etc. — owned by Accounts.' },
  { key: 'EXPENSE_CAPTURE', label: 'Purchase', description: 'Vendor bills from Expense Capture.' },
  { key: 'PEOPLE_ENGINE', label: 'People', description: 'Salary, Service Charge, Bonus.' },
  { key: 'STATUTORY', label: 'Statutory', description: 'GST, TDS, PF, ESI.' },
];

const emptyForm = {
  category: 'Rent',
  vendor_name: '',
  due_date: businessToday(),
  obligation_date: businessToday(),
  period_start: '',
  period_end: '',
  gross_amount: '',
  tax_amount: '',
  net_amount: '',
  expense_classification: 'EXPENSE',
  notes: '',
};
const errText = (err, f) => { const d = err?.response?.data?.detail; return typeof d === 'string' ? d : d?.message || f; };

function ObligationDialog({ open, onClose, onSaved, initial, categories }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          category: initial.category || 'Rent',
          vendor_name: initial.vendor_name || '',
          due_date: initial.due_date || businessToday(),
          obligation_date: initial.obligation_date || businessToday(),
          period_start: initial.period_start || '',
          period_end: initial.period_end || '',
          gross_amount: initial.gross_amount ?? '',
          tax_amount: initial.tax_amount ?? '',
          net_amount: initial.net_amount ?? '',
          expense_classification: initial.expense_classification || 'EXPENSE',
          notes: initial.notes || '',
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, initial]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.due_date) { toast.error('Due date is required'); return; }
    if (!form.net_amount && !form.gross_amount) { toast.error('Amount is required'); return; }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        vendor_name: form.vendor_name || undefined,
        obligation_date: form.obligation_date || undefined,
        due_date: form.due_date,
        period_start: form.period_start || undefined,
        period_end: form.period_end || undefined,
        gross_amount: form.gross_amount === '' ? undefined : Number(form.gross_amount),
        tax_amount: form.tax_amount === '' ? undefined : Number(form.tax_amount),
        net_amount: form.net_amount === '' ? undefined : Number(form.net_amount),
        expense_classification: form.expense_classification,
        notes: form.notes || undefined,
      };
      if (isEdit) {
        await accountsAPI.updateObligation(initial.obligation_id, payload);
        toast.success('Obligation updated');
      } else {
        await accountsAPI.createObligation(payload);
        toast.success('Obligation created');
      }
      onSaved();
    } catch (err) {
      toast.error(errText(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="obligation-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Obligation' : 'Add Operating Obligation'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[11px] text-zinc-400">Category</Label>
            <Select value={form.category} onValueChange={(v) => update({ category: v })}>
              <SelectTrigger className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="obligation-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-zinc-400">Vendor / Payee</Label>
            <Input
              value={form.vendor_name}
              onChange={(e) => update({ vendor_name: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm"
              placeholder="e.g. Goa Electricity Dept."
              data-testid="obligation-vendor-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Due Date *</Label>
            <Input type="date" value={form.due_date} onChange={(e) => update({ due_date: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="obligation-due-date-input" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Obligation Date</Label>
            <Input type="date" value={form.obligation_date} onChange={(e) => update({ obligation_date: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Period Start</Label>
            <Input type="date" value={form.period_start} onChange={(e) => update({ period_start: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Period End</Label>
            <Input type="date" value={form.period_end} onChange={(e) => update({ period_end: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Gross Amount (₹)</Label>
            <Input type="number" step="0.01" value={form.gross_amount}
              onChange={(e) => update({ gross_amount: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="obligation-gross-input" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Tax (₹)</Label>
            <Input type="number" step="0.01" value={form.tax_amount}
              onChange={(e) => update({ tax_amount: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-zinc-400">Net Amount (₹) *</Label>
            <Input type="number" step="0.01" value={form.net_amount}
              onChange={(e) => update({ net_amount: e.target.value })}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="obligation-net-input" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-zinc-400">Accounting treatment</Label>
            <Select value={form.expense_classification} onValueChange={(v) => update({ expense_classification: v })}>
              <SelectTrigger className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="obligation-classification-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="EXPENSE">Expense — recognised over its service period</SelectItem>
                <SelectItem value="NON_EXPENSE">Not an expense — deposit, advance or loan (liability only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-zinc-400">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => update({ notes: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}><X className="w-3 h-3 mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="obligation-save-btn">
            <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({ open, obligation, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(businessToday());
  const [saving, setSaving] = useState(false);
  const [payKey, setPayKey] = useState(null);

  useEffect(() => {
    if (open && obligation) {
      setPayKey(newPaymentKey());
      setAmount(String(obligation.balance_amount ?? ''));
      setDate(businessToday());
    }
  }, [open, obligation]);

  if (!obligation) return null;

  const save = async () => {
    const n = Number(amount);
    if (!n || n <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await accountsAPI.markObligationPaid(obligation.obligation_id, {
        paid_amount: n, payment_date: date,
      }, payKey);
      toast.success('Payment recorded');
      onSaved();
    } catch (err) {
      toast.error(errText(err, 'Payment failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-400">
            <div>{obligation.category} · {obligation.vendor_name || '—'}</div>
            <div>Balance: <span className="font-mono text-zinc-200">{fmtINR(obligation.balance_amount)}</span></div>
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Amount Paid (₹)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="mark-paid-amount-input" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Payment Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-9 bg-zinc-800 border-zinc-700 text-sm" data-testid="mark-paid-date-input" />
          </div>
          <p className="text-[10px] text-zinc-500">Records the payment for bookkeeping. It does not move money or confirm a bank transfer.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}><X className="w-3 h-3 mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black"
            data-testid="mark-paid-confirm-btn">
            <CheckCircle2 className="w-3 h-3 mr-1" /> {saving ? 'Recording…' : 'Record Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SummaryStat = ({ testid, icon: Icon, label, count, amount, color }) => (
  <Card className={`bg-zinc-900/60 border-zinc-800 ${color?.border || ''}`} data-testid={testid}>
    <CardContent className="py-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color?.bg || 'bg-zinc-800'}`}>
        <Icon className={`w-4 h-4 ${color?.icon || 'text-zinc-400'}`} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="text-lg font-mono text-zinc-100" data-testid={`${testid}-amount`}>{fmtINR(amount)}</div>
        <div className="text-[10px] text-zinc-500">{count} obligation{count === 1 ? '' : 's'}</div>
      </div>
    </CardContent>
  </Card>
);

export default function ObligationsPage() {
  const { user } = useAuth();
  const [source, setSource] = useState('OPERATING');
  const [rows, setRows] = useState([]);
  const [placeholder, setPlaceholder] = useState(null);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bucketFilter, setBucketFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('__all__');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paidOpen, setPaidOpen] = useState(false);
  const [payingRow, setPayingRow] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const perms = usePermissions();
  const begin = useLatestRequest();

  const load = useCallback(async () => {
    const isLatest = begin();
    setLoading(true);
    setError(null);
    try {
      const params = {};
      params.source = source;
      if (bucketFilter) params.bucket = bucketFilter;
      if (categoryFilter && categoryFilter !== '__all__') params.category = categoryFilter;
      const [listRes, sumRes, catRes] = await Promise.all([
        accountsAPI.listObligations(params),
        source === 'OPERATING' ? accountsAPI.getObligationsSummary() : Promise.resolve({ data: null }),
        categories.length ? Promise.resolve({ data: { categories } }) : accountsAPI.getObligationCategories(),
      ]);
      if (!isLatest()) return;
      setRows(listRes.data?.obligations || []);
      setPlaceholder(
        listRes.data?.read_only_placeholder
          ? listRes.data.placeholder_message || 'Coming soon.'
          : null,
      );
      setSummary(sumRes.data);
      if (catRes.data?.categories && !categories.length) setCategories(catRes.data.categories);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (isLatest()) setError(errText(err, 'Failed to load obligations'));
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [source, bucketFilter, categoryFilter, categories, begin]);

  useEffect(() => { load(); }, [load]);

  const onCreated = () => { setDialogOpen(false); setEditing(null); load(); };
  const onPaid = () => { setPaidOpen(false); setPayingRow(null); load(); };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.category} — ${row.vendor_name || 'no vendor'}"? This cannot be undone.`)) return;
    try {
      await accountsAPI.deleteObligation(row.obligation_id);
      toast.success('Obligation deleted');
      load();
    } catch (err) {
      toast.error(errText(err, 'Delete failed'));
    }
  };

  const buckets = summary?.buckets || {};
  const outletId = user?.outlet_id || '';

  const filteredRows = useMemo(() => rows, [rows]);
  const exportRows = () => downloadCsv(`obligations-${source.toLowerCase()}-${businessToday()}.csv`, [
    { label: 'Obligation ID', value: 'obligation_id' }, { label: 'Category', value: 'category' },
    { label: 'Vendor', value: 'vendor_name' }, { label: 'Obligation date', value: 'obligation_date' },
    { label: 'Due date', value: 'due_date' }, { label: 'Period start', value: 'period_start' },
    { label: 'Period end', value: 'period_end' }, { label: 'Treatment', value: (r) => r.expense_classification || 'EXPENSE' },
    { label: 'Net', value: 'net_amount' }, { label: 'Paid', value: 'paid_amount' },
    { label: 'Balance', value: 'balance_amount' }, { label: 'Status', value: 'display_status' },
  ], filteredRows);

  return (
    <div className="space-y-6" data-testid="obligations-page">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-400" />
            Financial Obligations
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            What the business owes, to whom, and by when. Operating obligations only in Phase 0 —
            Purchase / People / Statutory sources arrive as read-only feeds in a later phase.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <InlineIntegrityChip surface="obligations" />
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            disabled={source !== 'OPERATING' || !perms.financial}
            title={perms.financial ? undefined : 'Requires ORG_ADMIN or OWNER'}
            data-testid="add-obligation-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Add Obligation
          </Button>
        </div>
      </div>

      {/* Summary strip (Operating only) */}
      {source === 'OPERATING' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="obligations-summary-strip">
          <SummaryStat
            testid="summary-overdue" icon={AlertTriangle} label="Overdue"
            count={buckets.overdue?.count || 0} amount={buckets.overdue?.amount || 0}
            color={{ bg: 'bg-rose-500/10', icon: 'text-rose-400', border: 'border-rose-500/20' }}
          />
          <SummaryStat
            testid="summary-due-today" icon={CalendarClock} label="Due Today"
            count={buckets.due_today?.count || 0} amount={buckets.due_today?.amount || 0}
            color={{ bg: 'bg-amber-500/10', icon: 'text-amber-400', border: 'border-amber-500/20' }}
          />
          <SummaryStat
            testid="summary-due-this-week" icon={Clock} label="Due This Week"
            count={buckets.due_this_week?.count || 0} amount={buckets.due_this_week?.amount || 0}
            color={{ bg: 'bg-sky-500/10', icon: 'text-sky-400' }}
          />
          <SummaryStat
            testid="summary-outstanding" icon={Receipt} label="Total Outstanding"
            count={
              (buckets.overdue?.count || 0) + (buckets.due_today?.count || 0) +
              (buckets.due_this_week?.count || 0) + (buckets.upcoming?.count || 0)
            }
            amount={summary?.total_outstanding || 0}
            color={{ bg: 'bg-emerald-500/10', icon: 'text-emerald-400' }}
          />
        </div>
      )}

      {source === 'OPERATING' && <ObligationEffectsPanel refreshKey={refreshKey} />}

      {/* Source tabs */}
      <div className="flex items-center gap-2 border-b border-zinc-800" data-testid="obligations-source-tabs">
        {SOURCE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setSource(t.key); setBucketFilter(null); setCategoryFilter('__all__'); }}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              source === t.key ? 'border-amber-400 text-zinc-100' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`source-tab-${t.key.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters — Operating only */}
      {source === 'OPERATING' && (
        <div className="flex flex-wrap items-center gap-2" data-testid="obligations-filters">
          <div className="flex items-center gap-1">
            {[
              ['ALL', null], ['OVERDUE', 'OVERDUE'], ['DUE_TODAY', 'DUE_TODAY'],
              ['DUE_THIS_WEEK', 'DUE_THIS_WEEK'], ['UPCOMING', 'UPCOMING'],
            ].map(([label, val]) => (
              <button
                key={label}
                onClick={() => setBucketFilter(val)}
                className={`text-[11px] px-2 py-1 rounded border ${
                  (bucketFilter || 'ALL') === (val || 'ALL')
                    ? 'border-amber-500/40 text-amber-300 bg-amber-500/10'
                    : 'border-zinc-800 text-zinc-500 hover:text-zinc-300'
                }`}
                data-testid={`bucket-filter-${(val || 'ALL').toLowerCase()}`}
              >
                {label.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 w-48 bg-zinc-900 border-zinc-800 text-xs" data-testid="category-filter-select">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="__all__">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Register */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-zinc-300 font-medium flex items-center justify-between">
            <span>{SOURCE_TABS.find((t) => t.key === source)?.label} Obligations</span>
            {!placeholder && filteredRows.length > 0 && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportRows} data-testid="obligations-export-btn">
                <Download className="w-3 h-3 mr-1" /> Export {filteredRows.length} rows
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
            </div>
          ) : error ? (
            <div className="text-xs text-rose-300 flex items-center gap-2" data-testid="obligations-load-error">
              <AlertTriangle className="w-4 h-4" /> {error}
              <Button size="sm" variant="outline" className="text-xs ml-auto" onClick={load}>Retry</Button>
            </div>
          ) : placeholder ? (
            <div
              className="flex items-start gap-3 rounded-md border border-zinc-800 border-dashed p-4 text-xs text-zinc-500"
              data-testid={`placeholder-${source.toLowerCase()}`}
            >
              <Info className="w-4 h-4 mt-0.5 text-zinc-500 shrink-0" />
              <div>
                <p className="text-zinc-300 font-medium">{SOURCE_TABS.find((t) => t.key === source)?.label} obligations</p>
                <p className="mt-1">{placeholder}</p>
              </div>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center text-zinc-500 text-sm py-8" data-testid="obligations-empty">
              No obligations in this view. Click <span className="text-zinc-300">Add Obligation</span> to create one.
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/60">
              {filteredRows.map((row) => {
                const badge = STATUS_STYLES[row.display_status] || STATUS_STYLES.SCHEDULED;
                return (
                  <div
                    key={row.obligation_id}
                    className="py-3 flex flex-wrap items-center gap-3"
                    data-testid={`obligation-row-${row.obligation_id}`}
                  >
                    <div className="min-w-[10rem]">
                      <p className="text-sm text-zinc-200">{row.category}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{row.vendor_name || 'No vendor'}</p>
                    </div>
                    <div className="text-xs text-zinc-400 min-w-[7rem]">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Due</div>
                      {dateLabel(row.due_date)}
                    </div>
                    <div className="text-xs font-mono text-zinc-200 min-w-[7rem]">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Net</div>
                      {fmtINR(row.net_amount)}
                    </div>
                    <div className="text-xs font-mono text-zinc-200 min-w-[7rem]">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Paid</div>
                      {fmtINR(row.paid_amount)}
                    </div>
                    <div className="text-xs font-mono text-zinc-100 min-w-[7rem]">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Balance</div>
                      <span data-testid={`balance-${row.obligation_id}`}>{fmtINR(row.balance_amount)}</span>
                    </div>
                    <Badge className={`text-[10px] border ${badge.color}`} data-testid={`status-${row.obligation_id}`}>
                      {badge.label}
                    </Badge>
                    <div className="ml-auto flex items-center gap-2">
                      <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setDetailId(row.obligation_id)}
                        data-testid={`detail-btn-${row.obligation_id}`}>
                        <Eye className="w-3 h-3" />
                      </Button>
                      {perms.financial && row.payment_status !== 'PAID' && row.display_status !== 'CANCELLED' && (
                        <Button
                          size="sm" variant="outline"
                          className="text-xs h-7 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          onClick={() => { setPayingRow(row); setPaidOpen(true); }}
                          data-testid={`mark-paid-btn-${row.obligation_id}`}
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid
                        </Button>
                      )}
                      {perms.financial && row.display_status !== 'CANCELLED' && <Button
                        size="sm" variant="outline"
                        className="text-xs h-7"
                        onClick={() => { setEditing(row); setDialogOpen(true); }}
                        data-testid={`edit-btn-${row.obligation_id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>}
                      {perms.financial && !(row.payments || []).length && (
                        <Button
                          size="sm" variant="ghost"
                          className="text-xs h-7 text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                          onClick={() => remove(row)}
                          data-testid={`delete-btn-${row.obligation_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-zinc-500" data-testid="obligations-help-note">
        Scope: <span className="text-zinc-300">Operating obligations only</span> in Phase 0
        (outlet {outletId ? outletId.slice(0, 8) : 'unknown'}). Recurrence, attachments, bill-backed
        obligations and statutory items are deferred. Items with payment history can be cancelled, not deleted.
      </p>

      <ObligationDialog
        open={dialogOpen} initial={editing} categories={categories}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={onCreated}
      />
      <ObligationDetailDialog obligationId={detailId} canAct={perms.financial}
        onClose={() => setDetailId(null)} onChanged={load} />
      <MarkPaidDialog
        open={paidOpen} obligation={payingRow}
        onClose={() => { setPaidOpen(false); setPayingRow(null); }}
        onSaved={onPaid}
      />
    </div>
  );
}
